import type { PageResult, SplitSummary } from './types';
import { sourceBaseName } from './normalize';

export interface ProcessingReport {
  jobId?: string;
  sourceFileName: string;
  sourceBaseName: string;
  sourcePageCount: number;
  status: 'completed' | 'completed_with_warnings' | 'failed';
  createdAt?: string;
  completedAt?: string;
  summary: SplitSummary;
  pageResults: PageResult[];
}

/** Build the JSON processing report (PRD §10.1). */
export function buildJsonReport(
  sourceFileName: string,
  summary: SplitSummary,
  pageResults: PageResult[],
  extra: Partial<Pick<ProcessingReport, 'jobId' | 'createdAt' | 'completedAt'>> = {},
): string {
  const report: ProcessingReport = {
    jobId: extra.jobId,
    sourceFileName,
    sourceBaseName: sourceBaseName(sourceFileName),
    sourcePageCount: summary.sourcePageCount,
    status:
      summary.unresolvedPageCount > 0 || summary.needsReviewCount > 0
        ? 'completed_with_warnings'
        : 'completed',
    createdAt: extra.createdAt,
    completedAt: extra.completedAt,
    summary,
    pageResults,
  };
  return JSON.stringify(report, null, 2);
}

/** Build a CSV report mapping every page to its output file. */
export function buildCsvReport(
  sourceFileName: string,
  pageResults: PageResult[],
  outputFileNameForPage: Map<number, string>,
): string {
  const esc = (v: string | number | null | undefined): string => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    'source_file',
    'page_number',
    'tracking_number',
    'po_number',
    'status',
    'output_file',
    'warnings',
  ];
  const rows = [...pageResults]
    .sort((a, b) => a.pageNumber - b.pageNumber)
    .map((p) =>
      [
        sourceFileName,
        p.pageNumber,
        p.tracking ?? '',
        p.po ?? '',
        p.status,
        outputFileNameForPage.get(p.pageNumber) ?? '',
        p.warnings.join('; '),
      ]
        .map(esc)
        .join(','),
    );
  return [header.join(','), ...rows].join('\r\n');
}

/** Build the page -> output file mapping used by the CSV report and UI. */
export function buildPageMapping(
  pageResults: PageResult[],
  outputs: Array<{ fileName: string; pageNumbers: number[] }>,
): Map<number, string> {
  const map = new Map<number, string>();
  for (const output of outputs) {
    for (const page of output.pageNumbers) map.set(page, output.fileName);
  }
  for (const page of pageResults) {
    if (!map.has(page.pageNumber)) map.set(page.pageNumber, '(未分配)');
  }
  return map;
}
